import {Button, CtaDock} from '@chekinapp/ui';
import {ReactNode, useEffect, useMemo} from 'react';
import cn from 'clsx';
import {Trans, useTranslation} from 'react-i18next';
import {Controller, useFormContext} from 'react-hook-form';
import {FormTypes} from './types';
import {FORM_NAMES} from './constants';
import {GuestGroup} from '@chekin/core';
import {getFields} from './utils';
import {getLabels, type SeasonRules} from './labels';
import {CounterStepper} from './CounterStepper';

type NumberOfGuestsFormProps = {
  isLoading: boolean;
  seasonEntity: SeasonRules;
  ageLimit?: number | null;
  allowEditingGuestNumber?: boolean;
  defaultNumberOfPets?: number;
  guestGroup?: GuestGroup;
  disabled?: boolean;
  onSubmit: () => void;
  canEdit?: boolean | null;
  showPetsCounter?: boolean;
  className?: string;
  submitLabel?: string | ((totalGuests: number) => string);
  renderHeader?: (totalGuests: number) => ReactNode;
  renderBeforeSubmit?: (totalGuests: number) => ReactNode;
  stickySubmit?: boolean;
  icons?: Partial<Record<FORM_NAMES, ReactNode>>;
  petsSubtitle?: ReactNode;
  grouped?: boolean;
  loadingLabel?: string;
  onTotalGuestsChange?: (totalGuests: number) => void;
};

function NumberOfGuestsForm({
  ageLimit,
  guestGroup,
  isLoading,
  disabled,
  onSubmit,
  canEdit,
  seasonEntity,
  showPetsCounter,
  className,
  submitLabel,
  renderHeader,
  renderBeforeSubmit,
  stickySubmit,
  icons,
  petsSubtitle,
  grouped,
  loadingLabel,
  onTotalGuestsChange,
  allowEditingGuestNumber,
  defaultNumberOfPets,
}: NumberOfGuestsFormProps) {
  const {t} = useTranslation();
  const {control, handleSubmit, watch, reset, getValues} = useFormContext<FormTypes>();
  const totalJuniors = watch(FORM_NAMES.junior);
  const totalMiddle = watch(FORM_NAMES.middle);
  const totalSeniors = watch(FORM_NAMES.senior);
  const singleFieldTotal = watch(FORM_NAMES.numberOfGuests);
  const numberOfGuests = (totalJuniors ?? 0) + (totalMiddle ?? 0) + (totalSeniors ?? 0);

  const visibleFields = useMemo(
    () =>
      getFields({
        lessThanRule: seasonEntity.lessThanRule,
        betweenRule: seasonEntity.betweenRule,
        moreThanRule: seasonEntity.moreThanRule,
      }),
    [seasonEntity],
  );

  const labels = getLabels({
    lessThanRule: seasonEntity.lessThanRule,
    betweenRule: seasonEntity.betweenRule,
    moreThanRule: seasonEntity.moreThanRule,
    visibleFields,
    t,
  });

  useEffect(
    function preload() {
      if (typeof guestGroup?.known_number_of_guests !== 'number') return;
      const isNotDefinedAnyGroup =
        !guestGroup?.children && !guestGroup?.adults && !guestGroup?.seniors;
      const currentValues = getValues();
      const numberOfPets =
        currentValues[FORM_NAMES.numberOfPets] ?? defaultNumberOfPets ?? 0;

      if (isNotDefinedAnyGroup) {
        reset({
          ...currentValues,
          [FORM_NAMES.numberOfGuests]: guestGroup?.known_number_of_guests ?? 0,
          [FORM_NAMES.numberOfPets]: numberOfPets,
          [FORM_NAMES.junior]: 0,
          [FORM_NAMES.middle]: visibleFields[FORM_NAMES.middle]
            ? guestGroup?.known_number_of_guests ?? 0
            : 0,
          [FORM_NAMES.senior]: !visibleFields[FORM_NAMES.middle]
            ? guestGroup?.known_number_of_guests ?? 0
            : 0,
        });
        return;
      }
      reset({
        ...currentValues,
        [FORM_NAMES.numberOfGuests]: guestGroup?.known_number_of_guests ?? 0,
        [FORM_NAMES.numberOfPets]: numberOfPets,
        [FORM_NAMES.junior]: guestGroup?.children ?? 0,
        [FORM_NAMES.middle]: guestGroup?.adults ?? 0,
        [FORM_NAMES.senior]: guestGroup?.seniors ?? 0,
      });
    },
    [
      guestGroup?.adults,
      guestGroup?.seniors,
      guestGroup?.children,
      guestGroup?.known_number_of_guests,
      visibleFields,
      reset,
      getValues,
      defaultNumberOfPets,
    ],
  );

  const isDisabled = disabled || isLoading;

  const isLimitOfGuestsExceeded =
    !allowEditingGuestNumber &&
    !!seasonEntity &&
    Boolean((guestGroup?.known_number_of_guests ?? 0) < numberOfGuests);

  const fieldsCount = Object.keys(visibleFields).length + (showPetsCounter ? 1 : 0);
  const fieldsNumberClassName = fieldsCount > 1 ? 'multiple-fields' : 'single-field';

  const allowedGuests = !allowEditingGuestNumber
    ? guestGroup?.known_number_of_guests
    : undefined;

  const totalGuests = visibleFields[FORM_NAMES.numberOfGuests]
    ? singleFieldTotal ?? 0
    : numberOfGuests;

  const showTotal =
    (visibleFields[FORM_NAMES.junior] ||
      visibleFields[FORM_NAMES.middle] ||
      visibleFields[FORM_NAMES.senior]) &&
    (!grouped || typeof allowedGuests === 'number');

  useEffect(
    function reportTotalGuests() {
      onTotalGuestsChange?.(totalGuests);
    },
    [onTotalGuestsChange, totalGuests],
  );

  const submitButton = (
    <Button
      disabled={disabled || isLimitOfGuestsExceeded}
      loading={isLoading}
      loadingText={loadingLabel}
      onClick={handleSubmit(onSubmit)}
      className={cn(
        'number-of-guests-form__button',
        'flex w-full max-w-none',
        !stickySubmit && 'mt-5',
      )}
    >
      {typeof submitLabel === 'function'
        ? submitLabel(totalGuests)
        : submitLabel ?? t('save_changes')}
    </Button>
  );

  return (
    <div
      className={cn('number-of-guests-form__fields', className, fieldsNumberClassName)}
    >
      {renderHeader?.(totalGuests)}
      <div
        className={cn(
          'flex flex-col',
          grouped
            ? 'divide-y divide-[var(--chekin-divider-subtle)] rounded-[18px] bg-[var(--chekin-color-surface-pressed)] px-4 py-1'
            : 'gap-2.5',
        )}
      >
        {visibleFields[FORM_NAMES.numberOfGuests] && (
          <Controller
            name={FORM_NAMES.numberOfGuests}
            control={control}
            render={({field, fieldState: {error}}) => {
              return (
                <CounterStepper
                  title={t('number_of_guests')}
                  subtitle={ageLimit && t('age_limit_years_old', {ageLimit})}
                  icon={icons?.[FORM_NAMES.numberOfGuests]}
                  error={error?.message}
                  disabled={isDisabled}
                  readOnly={!canEdit}
                  grouped={grouped}
                  {...field}
                />
              );
            }}
          />
        )}
        {visibleFields[FORM_NAMES.middle] && (
          <Controller
            name={FORM_NAMES.middle}
            control={control}
            render={({field, fieldState: {error}}) => {
              return (
                <CounterStepper
                  title={t('adults')}
                  subtitle={labels.middle}
                  icon={icons?.[FORM_NAMES.middle]}
                  disabled={isDisabled}
                  readOnly={!canEdit}
                  grouped={grouped}
                  error={error?.message}
                  {...field}
                />
              );
            }}
          />
        )}
        {visibleFields[FORM_NAMES.senior] && (
          <Controller
            name={FORM_NAMES.senior}
            control={control}
            render={({field, fieldState: {error}}) => {
              return (
                <CounterStepper
                  title={t('seniors')}
                  subtitle={labels.senior}
                  icon={icons?.[FORM_NAMES.senior]}
                  error={error?.message}
                  readOnly={!canEdit}
                  grouped={grouped}
                  disabled={isDisabled}
                  {...field}
                />
              );
            }}
          />
        )}
        {visibleFields[FORM_NAMES.junior] && (
          <Controller
            name={FORM_NAMES.junior}
            control={control}
            render={({field, fieldState: {error}}) => {
              return (
                <CounterStepper
                  title={t('minors')}
                  subtitle={labels.junior}
                  icon={icons?.[FORM_NAMES.junior]}
                  disabled={isDisabled}
                  readOnly={!canEdit}
                  grouped={grouped}
                  error={error?.message}
                  {...field}
                />
              );
            }}
          />
        )}
        {showPetsCounter && (
          <Controller
            name={FORM_NAMES.numberOfPets}
            control={control}
            render={({field, fieldState: {error}}) => {
              return (
                <CounterStepper
                  title={t('number_of_pets', {defaultValue: 'Number of pets'})}
                  subtitle={petsSubtitle}
                  icon={icons?.[FORM_NAMES.numberOfPets]}
                  error={error?.message}
                  readOnly={!canEdit}
                  grouped={grouped}
                  disabled={isDisabled}
                  {...field}
                />
              );
            }}
          />
        )}
      </div>

      {showTotal && (
        <div className="mt-3.5 flex items-center justify-between px-1 text-[14px] font-semibold text-[var(--chekin-text-secondary)]">
          <span>{t('total_guests')}</span>
          <span
            className={cn(isLimitOfGuestsExceeded && 'text-[var(--chekin-red-soft)]')}
          >
            {typeof allowedGuests === 'number' ? (
              <Trans
                i18nKey="guests_of_allowed"
                values={{total: numberOfGuests, max: allowedGuests}}
                components={[
                  <span
                    key="0"
                    className="font-semibold text-[var(--chekin-color-brand-navy)]"
                  />,
                ]}
              />
            ) : (
              <span className="font-semibold text-[var(--chekin-color-brand-navy)]">
                {numberOfGuests}
              </span>
            )}
          </span>
        </div>
      )}

      {renderBeforeSubmit?.(totalGuests)}
      {stickySubmit ? (
        <CtaDock elevated className="mt-2 px-0 [--cta-dock-elevated-shadow:none]">
          {submitButton}
        </CtaDock>
      ) : (
        submitButton
      )}
    </div>
  );
}

export {NumberOfGuestsForm};
